import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import { BLOG_CATEGORIES, BlogCategory } from '../../types/blog';
import { useSEO, generateBlogSchema, generateBreadcrumbSchema } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import ShareButtons from '../../components/feature/ShareButtons';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState<BlogCategory | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO Configuration
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';
  
  useSEO({
    title: 'Trade Tips & Industry Insights - MtaPlannerPro Blog',
    description: 'Expert advice for tradies: HVAC maintenance, plumbing guides, electrical safety, carpentry techniques, business tips, and construction industry news. Learn from the pros and grow your trade business.',
    keywords: 'trade tips, HVAC maintenance, plumbing guides, electrical safety, carpentry techniques, tradie business tips, construction industry news, contractor advice, trade business growth',
    canonical: `${siteUrl}/blog`,
    ogTitle: 'Trade Tips & Industry Insights - MtaPlannerPro Blog',
    ogDescription: 'Expert advice for tradies: HVAC, plumbing, electrical, carpentry tips and business insights.',
    ogUrl: `${siteUrl}/blog`,
    structuredData: [
      generateBlogSchema({
        name: 'MtaPlannerPro Blog - Trade Tips & Insights',
        description: 'Expert advice, industry insights, and practical tips to help you grow your trade business and master your craft.',
        url: '/blog'
      }),
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Blog', url: '/blog' }
      ])
    ]
  });

  const filteredPosts = useMemo(() => {
    let filtered = [...blogPosts];

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [selectedCategory, searchQuery]);

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  const getCategoryColor = (category: BlogCategory) => {
    const colors: Record<BlogCategory, string> = {
      'HVAC Tips': 'bg-blue-100 text-blue-700',
      'Plumbing Guides': 'bg-cyan-100 text-cyan-700',
      'Electrical Safety': 'bg-yellow-100 text-yellow-700',
      'Carpentry Tricks': 'bg-orange-100 text-orange-700',
      'Business Tips': 'bg-purple-100 text-purple-700',
      'Industry News': 'bg-green-100 text-green-700'
    };
    return colors[category];
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
            <i className="ri-article-line text-amber-600"></i>
            <span className="text-sm font-medium text-amber-700">Trade Tips &amp; Insights</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Learn from the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">Pros</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Expert advice, industry insights, and practical tips to help you grow your trade business and master your craft.
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-2xl mx-auto">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg"></i>
              <input
                type="text"
                placeholder="Search articles, tips, guides..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors text-base"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-6 py-2.5 rounded-full font-medium transition-all whitespace-nowrap ${
                selectedCategory === 'All'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                  : 'bg-white text-slate-600 hover:bg-slate-50 border-2 border-slate-200'
              }`}
            >
              All Articles
            </button>
            {BLOG_CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2.5 rounded-full font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border-2 border-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Results Count */}
          <div className="text-center mt-8">
            <p className="text-slate-600">
              Showing <strong className="text-slate-900">{filteredPosts.length}</strong> {filteredPosts.length === 1 ? 'article' : 'articles'}
              {selectedCategory !== 'All' && <span> in <strong className="text-amber-600">{selectedCategory}</strong></span>}
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          {currentPosts.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-file-search-line text-6xl text-slate-300 mb-4"></i>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">No articles found</h3>
              <p className="text-slate-600">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {currentPosts.map((post, index) => (
                  <article
                    key={post.id}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
                    style={{
                      animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                    }}
                  >
                    {/* Featured Image */}
                    <Link to={`/blog/${post.slug}`} className="block relative overflow-hidden aspect-[16/10]">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </Link>

                    {/* Content */}
                    <div className="p-6">
                      {/* Category Badge */}
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(post.category)}`}>
                          {post.category}
                        </span>
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {post.readTime} min read
                        </span>
                      </div>

                      {/* Title */}
                      <Link to={`/blog/${post.slug}`}>
                        <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                      </Link>

                      {/* Excerpt */}
                      <p className="text-slate-600 mb-4 line-clamp-3 text-sm leading-relaxed">
                        {post.excerpt}
                      </p>

                      {/* Author & Date */}
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
                            <img
                              src={post.author.avatar}
                              alt={post.author.name}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{post.author.name}</p>
                            <p className="text-xs text-slate-500">{post.author.role}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <ShareButtons
                            url={`/blog/${post.slug}`}
                            title={post.title}
                            size="sm"
                            variant="dropdown"
                          />
                          <Link
                            to={`/blog/${post.slug}`}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 group-hover:bg-amber-500 group-hover:text-white transition-all cursor-pointer"
                          >
                            <i className="ri-arrow-right-line"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-16">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border-2 border-slate-200 text-slate-600 hover:border-amber-500 hover:text-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                        currentPage === page
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                          : 'bg-white border-2 border-slate-200 text-slate-600 hover:border-amber-500 hover:text-amber-600'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border-2 border-slate-200 text-slate-600 hover:border-amber-500 hover:text-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
